import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, TrendingUp, Camera, Video, Globe, BarChart3, Sparkles, Star, Mail, User, Phone, MessageSquare, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import TaveelLogo from '../components/TaveelLogo';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [showStickyButton, setShowStickyButton] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    package: 'Still deciding',
    honeypot: '', // Spam protection
    page_url: window.location.href
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Sticky button scroll handler
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyButton(window.scrollY > 800);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Counter animation hook
  const useCounter = (end, duration = 2000, start = 0) => {
    const [count, setCount] = useState(start);
    const countRef = useRef(null);
    const observerRef = useRef(null);

    useEffect(() => {
      const element = countRef.current;
      
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            const startTime = Date.now();
            const timer = setInterval(() => {
              const now = Date.now();
              const progress = Math.min((now - startTime) / duration, 1);
              const value = start + (end - start) * progress;
              
              if (progress === 1) {
                clearInterval(timer);
                setCount(end);
              } else {
                setCount(value);
              }
            }, 16);

            return () => clearInterval(timer);
          }
        },
        { threshold: 0.5 }
      );

      if (element) {
        observerRef.current.observe(element);
      }

      return () => {
        if (observerRef.current && element) {
          observerRef.current.unobserve(element);
        }
      };
    }, [end, duration, start]);

    return { count, countRef };
  };

  const stats = [
    { number: '100+', label: 'Businesses Scaled' },
    { number: '16+', label: 'Industries Served' },
    { number: '185%', label: 'Average ROI Increase' },
    { number: '24/7', label: 'Support Available' }
  ];

  const services = [
    {
      icon: <TrendingUp className="w-10 h-10 animate-trending" />,
      title: 'AI-Powered Trend Analysis',
      description: "We leverage cutting-edge AI technology to identify what's trending in your specific niche, ensuring your content stays ahead of the curve and resonates with your target audience."
    },
    {
      icon: <Camera className="w-10 h-10 animate-camera" />,
      title: 'In-House Creative Team',
      description: "Our professional photographers and videographers create stunning, conversion-focused content that captures your brand's essence and drives engagement."
    },
    {
      icon: <Video className="w-10 h-10 animate-video" />,
      title: 'Custom Ad Creation',
      description: 'Every ad is crafted specifically for your business, tested, and optimized for maximum ROI across all platforms including Meta, Google, and TikTok.'
    },
    {
      icon: <Globe className="w-10 h-10 animate-globe" />,
      title: 'Website Development',
      description: "We build modern, conversion-optimized websites that perfectly represent your brand and turn visitors into customers with seamless user experiences."
    },
    {
      icon: <BarChart3 className="w-10 h-10 animate-chart" />,
      title: 'Data-Driven Strategy',
      description: "With thousands of businesses served across hundreds of niches, we know what works. Our strategies are backed by real data and proven results."
    }
  ];

  const trialFeatures = [
    { title: 'Custom Ads', description: 'Custom ads created for your business' },
    { title: 'Zero Risk', description: 'No credit card required to start' },
    { title: 'Full Access', description: 'Experience our complete service' }
  ];

  const adExamples = [
    {
      category: 'Dental',
      title: '$79 New Patient Special',
      subtitle: 'Includes Exam, X-rays & Cleaning',
      cta: 'Book Now',
      metric: 340,
      metricLabel: '% increase in bookings',
      bgColor: 'bg-blue-900',
      image: 'https://images.unsplash.com/photo-1704455306251-b4634215d98f?w=400&h=500&fit=crop&q=80'
    },
    {
      category: 'Fashion',
      title: 'Sustainable Style That Turns Heads',
      subtitle: 'New collection drops Friday',
      cta: 'Shop Now',
      metric: 4.2,
      metricLabel: 'x ROAS',
      bgColor: 'bg-purple-900',
      image: 'https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?w=400&h=500&fit=crop&q=80'
    },
    {
      category: 'Fitness',
      title: 'Transform Your Body in 90 Days',
      subtitle: 'First month 50% off',
      cta: 'Start Today',
      metric: 220,
      metricLabel: '+ new members',
      bgColor: 'bg-green-900',
      image: 'https://images.pexels.com/photos/841130/pexels-photo-841130.jpeg?w=400&h=500&fit=crop'
    },
    {
      category: 'Venues',
      title: 'Where Dreams Become Memories',
      subtitle: '2025 dates filling fast',
      cta: 'Schedule Tour',
      metric: 85,
      metricLabel: '% booking rate',
      bgColor: 'bg-pink-900',
      image: 'https://images.unsplash.com/photo-1505944357431-27579db47558?w=400&h=500&fit=crop&q=80'
    }
  ];

  const industries = [
    {
      name: 'Event Venues',
      activeClients: 8,
      issues: [
        'Your venue photos lack the emotional storytelling that makes couples envision their big day',
        'Ad targeting is too generic instead of focusing on newly engaged couples in your price range',
        'Missing seasonal booking urgency and limited-date-availability messaging',
        'No video walkthrough content showing the venue transformation throughout an event'
      ]
    },
    {
      name: 'Dental Clinics',
      activeClients: 12,
      issues: [
        'Generic stock images instead of authentic before/after patient transformations',
        'Ad copy fails to address dental anxiety and fear that stops bookings',
        'No emphasis on modern technology, comfort features, or same-day availability',
        'Missing trust signals like real patient reviews and credentialed team photos'
      ]
    },
    {
      name: 'Clothing Brands',
      activeClients: 15,
      issues: [
        'Flat product photography without lifestyle context or movement',
        'Not leveraging user-generated content from real customers wearing your pieces',
        'Ignoring micro-seasonal trends and cultural moments for timely campaigns',
        'Missing size inclusivity and body diversity in ad creative'
      ]
    },
    {
      name: 'Fitness Centers',
      activeClients: 11,
      issues: [
        'Equipment showcase instead of member transformation stories and results',
        'No low-barrier trial offers or first-session incentives in your ads',
        'Missing the community vibe and group energy that drives membership',
        'Ads focus on intimidation rather than accessibility for beginners'
      ]
    },
    {
      name: 'Restaurants & Cafes',
      activeClients: 18,
      issues: [
        'Poor lighting in food photography making dishes look unappetizing',
        'Not highlighting chef specials, seasonal menus, or limited-time offers',
        'Failing to showcase the ambiance, vibe, and experience beyond just food',
        'No differentiation from competitors in your local market'
      ]
    },
    {
      name: 'Beauty Salons',
      activeClients: 14,
      issues: [
        'Inconsistent before/after quality and lighting that reduces credibility',
        'Not showcasing specific stylist expertise or signature techniques',
        'Missing online booking buttons that reduce friction to appointment',
        'No seasonal campaign alignment with major events (weddings, proms, holidays)'
      ]
    },
    {
      name: 'Medical Spas',
      activeClients: 9,
      issues: [
        'Too sterile and clinical messaging that misses the luxury experience angle',
        'Not educating prospects on treatment benefits, process, and safety',
        'Missing consultation incentives that lower the barrier to entry',
        'Ads lack aspirational lifestyle imagery that connects treatments to results'
      ]
    },
    {
      name: 'Real Estate Agencies',
      activeClients: 7,
      issues: [
        'Amateur property photos without professional staging or HDR quality',
        'One-size-fits-all messaging instead of speaking to first-time vs luxury buyers',
        'No virtual tour links or interactive property exploration options',
        'Missing hyper-local market expertise and neighborhood lifestyle content'
      ]
    },
    {
      name: 'Law Firms',
      activeClients: 5,
      issues: [
        'Legal jargon and corporate tone alienating potential clients who need help',
        'Not showcasing tangible case results or client success testimonials',
        'Failing to address specific pain points unique to each practice area',
        'No free consultation offers or clear next-step call-to-actions'
      ]
    },
    {
      name: 'E-commerce Stores',
      activeClients: 22,
      issues: [
        'Not retargeting cart abandoners with personalized product reminders',
        'Checkout experience has too much friction causing high drop-off rates',
        'Missing urgency triggers like limited stock, social proof, and countdown timers',
        'Product pages lack comprehensive reviews, sizing guides, and lifestyle imagery'
      ]
    },
    {
      name: 'Hotels & Resorts',
      activeClients: 6,
      issues: [
        'Not showcasing unique amenities and experiences that differentiate you',
        'Failing to segment ads by trip intent: business, leisure, romantic, family',
        'Missing seasonal packages, flash sales, and special occasion promotions',
        'No authentic guest-generated content showing real experiences'
      ]
    },
    {
      name: 'Wedding Planners',
      activeClients: 10,
      issues: [
        'Portfolio doesn\'t show full wedding journey from consultation to execution',
        'Targeting couples already booked instead of newly engaged prospects',
        'Not emphasizing stress reduction, budget management, and vendor relationships',
        'Missing specific niche positioning (luxury, budget-friendly, destination, etc.)'
      ]
    },
    {
      name: 'Automotive Dealerships',
      activeClients: 4,
      issues: [
        'Generic vehicle photos without lifestyle integration or emotional connection',
        'Not prominently featuring financing options and trade-in calculators',
        'Missing video walkthroughs, 360-degree views, and test drive booking',
        'Ads don\'t address specific buyer personas (families, eco-conscious, luxury)'
      ]
    },
    {
      name: 'Home Services',
      activeClients: 13,
      issues: [
        'No compelling before/after project documentation showing transformation',
        'Missing urgency offers like free estimates, same-day service, or seasonal discounts',
        'Not displaying licensing, insurance, warranty, and guarantee information',
        'Lack of neighborhood-specific targeting and local reputation building'
      ]
    },
    {
      name: 'Tech Startups',
      activeClients: 16,
      issues: [
        'Feature-dumping instead of benefit-driven messaging that solves real problems',
        'Unclear target user persona with vague pain point identification',
        'Missing social proof from recognizable brands, metrics, or testimonials',
        'No clear product demonstration or interactive trial experience in ads'
      ]
    },
    {
      name: 'Jewelry Stores',
      activeClients: 7,
      issues: [
        'Poor lighting and angles that fail to capture sparkle and craftsmanship',
        'Not creating emotional connections through occasion-based storytelling',
        'Missing customization options, virtual try-on, or personalization features',
        'Ads lack the trust signals needed for high-ticket luxury purchases'
      ]
    }
  ];

  const packages = [
    {
      name: 'STARTER',
      price: '$500',
      period: '/month',
      description: 'Perfect for testing the waters and seeing results',
      features: [
        '15 custom ads per month',
        'Basic AI trend analysis',
        '2 professional photoshoots',
        'Social media content calendar',
        'Monthly performance report',
        'Email support'
      ],
      popular: false
    },
    {
      name: 'PROFESSIONAL',
      price: '$1,497',
      period: '/month',
      description: 'The sweet spot - most businesses see 3x ROI at this level',
      features: [
        '40 custom ads per month',
        'Advanced AI trend analysis',
        '4 professional photoshoots',
        '2 videography sessions',
        'Weekly strategy calls',
        'Dedicated account manager',
        'Priority support',
        'Best value for scaling'
      ],
      popular: true
    },
    {
      name: 'ENTERPRISE',
      price: '$4,997',
      period: '/month',
      description: 'For businesses ready to dominate their market completely',
      features: [
        'Unlimited custom ads',
        'Real-time AI trend monitoring',
        'Unlimited photo & video content',
        'Multi-platform management',
        'Daily performance optimization',
        'Dedicated creative team',
        '24/7 priority support',
        'Quarterly strategy sessions'
      ],
      popular: false
    }
  ];

  const testimonials = [
    {
      quote: "TAVEEL transformed our online presence completely. Within 2 months, our patient bookings increased by 340%. Their AI-driven approach and in-house creative team are unmatched.",
      author: 'Sarah Martinez',
      company: 'Luxe Dental Clinic',
      rating: 5
    },
    {
      quote: "The free trial convinced us immediately. They created ads that converted 3x better than our previous agency. The ROI speaks for itself - we scaled from $50k to $200k monthly.",
      author: 'James Chen',
      company: 'Urban Threads Clothing',
      rating: 5
    },
    {
      quote: "Working with TAVEEL was a game-changer. Their photographers and videographers captured our space beautifully, and their AI trend analysis helped us target the right couples. Bookings up 280%.",
      author: 'Amanda Foster',
      company: 'The Grand Venue',
      rating: 5
    },
    {
      quote: "Best investment we've made. TAVEEL knew exactly what content would resonate with our audience. Their data-driven approach combined with creative excellence is unbeatable.",
      author: 'Michael Rodriguez',
      company: 'FitCore Gym',
      rating: 5
    }
  ];

  const clientLogos = [
    { name: 'Queen Mother Café', location: 'Toronto', industry: 'Restaurant' },
    { name: 'Club Monaco', location: 'Toronto', industry: 'Fashion' },
    { name: '6S Fitness+', location: 'Toronto', industry: 'Fitness' },
    { name: 'Tarami Fashion', location: 'Toronto', industry: 'Boutique' },
    { name: 'Fancy Franks', location: 'Toronto', industry: 'Restaurant' },
    { name: '6ix Tips Nails & Spa', location: 'Toronto', industry: 'Beauty' },
    { name: 'AAamazing Salad', location: 'Toronto', industry: 'Café' },
    { name: 'Pearl Dental', location: 'Toronto', industry: 'Dental' }
  ];

  const beforeAfterCases = [
    {
      industry: 'Dental Clinic',
      before: {
        image: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=600&h=400&fit=crop&q=80',
        metric: '0.3% CTR',
        description: 'Generic stock photo, vague messaging'
      },
      after: {
        image: 'https://images.unsplash.com/photo-1704455306251-b4634215d98f?w=600&h=400&fit=crop&q=80',
        metric: '2.8% CTR',
        description: 'Real office, specific offer, clear CTA'
      },
      result: '833% increase in click-through rate'
    },
    {
      industry: 'Fashion Brand',
      before: {
        image: 'https://images.unsplash.com/photo-1621198059871-0d5f9b449233?w=600&h=400&fit=crop&q=80',
        metric: '1.2x ROAS',
        description: 'Flat product shots, no context'
      },
      after: {
        image: 'https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?w=600&h=400&fit=crop&q=80',
        metric: '4.2x ROAS',
        description: 'Lifestyle imagery, seasonal urgency'
      },
      result: '250% increase in return on ad spend'
    }
  ];

  const faqs = [
    {
      question: "How is this different from other marketing agencies?",
      answer: "Most agencies outsource everything and use generic templates. We have in-house photographers, videographers, and AI analysts who create custom content specifically for YOUR business. Plus, our data from scaling 952+ businesses means we already know what works in your industry."
    },
    {
      question: "What if I've already tried ads and they didn't work?",
      answer: "That's exactly why we offer a free trial. 90% of failed ads share the same issues: poor creative, wrong targeting, or generic messaging. Our AI identifies what's trending in your niche RIGHT NOW, and our team creates ads proven to convert. See the difference yourself - no credit card required."
    },
    {
      question: "Is this really free? What's the catch?",
      answer: "Yes, completely free for 7 days. No catch. No credit card. We're so confident in our results that we let you experience everything first. Most businesses see the ROI immediately and choose to continue. We'd rather prove our value than lock you into a contract."
    },
    {
      question: "How quickly will I see results?",
      answer: "Most clients see improved ad performance within the first 2 weeks. Significant ROI increases typically happen within 30-60 days. Our fastest client hit 340% booking increase in just 8 weeks. Speed depends on your industry, but our AI-driven approach accelerates results dramatically."
    },
    {
      question: "I'm in a competitive market. Can you still help?",
      answer: "Competitive markets are where we thrive. Our advantage? We analyze what's already working in your space, then create something better. Custom content from our in-house team means you're never using the same stock photos as competitors. Plus, AI trend analysis keeps you ahead of the curve."
    },
    {
      question: "What if I don't have time to manage this?",
      answer: "That's the point - you don't have to. We handle everything: strategy, content creation, ad management, optimization, and reporting. You'll have a dedicated account manager (Professional & Enterprise plans) who keeps you updated. Your only job is to handle the increased business we bring you."
    },
    {
      question: "My budget is limited. Which package should I choose?",
      answer: "Start with our free trial to see results first. Most businesses find the Professional package hits the sweet spot - enough ads and content to scale significantly without overextending. Remember: one successful campaign pays for itself 10x over. It's not an expense, it's an investment."
    },
    {
      question: "How do I know this will work for MY specific business?",
      answer: "We've scaled businesses in 95+ industries with a 271% average ROI increase. Check the 'Industries We Scale' section - if your business is there, we've done it before and know exactly what works. Plus, the free trial lets you validate our approach risk-free before committing."
    }
  ];

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name || !formData.email || !formData.description) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields.',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      
      toast({
        title: 'Success!',
        description: 'Thanks — we\'ll reply within 24 hours.',
      });

      // Reset form
      setFormData({
        name: '',
        phone: '',
        email: '',
        description: '',
        package: 'Still deciding',
        honeypot: '',
        page_url: window.location.href
      });
    } catch (error) {
      const errorMessage = error.response?.status === 429 
        ? 'Too many submissions. Please try again later.'
        : 'Failed to send message. Please try again or email us directly.';
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1a1a] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#1a1a1a]/95 backdrop-blur-sm border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => window.location.href = '/'}>
            <TaveelLogo className="w-8 h-8" />
            <div className="text-2xl font-bold text-[#10B981]">TAVEEL</div>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('services')} className="hover:text-[#10B981] transition-colors">Services</button>
            <button onClick={() => scrollToSection('industries')} className="hover:text-[#10B981] transition-colors">Industries</button>
            <button onClick={() => scrollToSection('packages')} className="hover:text-[#10B981] transition-colors">Packages</button>
            <button onClick={() => scrollToSection('testimonials')} className="hover:text-[#10B981] transition-colors">Testimonials</button>
            <button onClick={() => window.location.href = '/why-taveel'} className="hover:text-[#10B981] transition-colors">Why Taveel</button>
            <Button 
              onClick={() => scrollToSection('contact')}
              className="bg-[#10B981] hover:bg-[#059669] text-white rounded-full px-6"
            >
              START FREE TRIAL
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#10B981]/30 bg-[#10B981]/10 mb-8 animate-hero-fade delay-100">
            <Sparkles className="w-4 h-4 text-[#10B981]" />
            <span className="text-sm text-[#10B981]">TRUSTED BY 100+ BUSINESSES</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight animate-hero-fade delay-200">
            SCALE YOUR BRAND.
            <br />
            <span className="text-[#10B981]">DOMINATE YOUR MARKET.</span>
          </h1>
          
          <p className="text-xl text-gray-400 mb-10 max-w-4xl mx-auto animate-hero-fade delay-300">
            AI-powered digital marketing with in-house photographers, videographers, website development, 
            and data-driven strategies. We've scaled thousands of businesses across hundreds of niches.
          </p>
          
          <Button 
            onClick={() => scrollToSection('contact')}
            className="bg-[#10B981] hover:bg-[#059669] text-white rounded-full px-8 py-6 text-lg animate-hero-fade delay-400"
          >
            Start Free 7-Day Trial
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20">
            {stats.map((stat, index) => {
              const StatCounter = () => {
                // Extract numeric value from stat.number
                const numMatch = stat.number.match(/(\d+)/);
                const endValue = numMatch ? parseInt(numMatch[1]) : 0;
                const suffix = stat.number.replace(/\d+/, '');
                
                const { count, countRef } = useCounter(endValue, 2000);
                const displayValue = Math.floor(count);
                
                return (
                  <div key={index} ref={countRef} className="text-center">
                    <div className="text-4xl md:text-5xl font-bold text-[#22C55E] mb-2">
                      {displayValue}{suffix}
                    </div>
                    <div className="text-sm text-gray-400 uppercase tracking-wide">{stat.label}</div>
                  </div>
                );
              };
              
              return <StatCounter key={index} />;
            })}
          </div>
        </div>
      </section>

      {/* Client Logos Section - Sliding Banner */}
      <section className="py-16 px-6 bg-[#2d2d2d] overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-sm text-gray-400 uppercase tracking-wider">TRUSTED BY LOCAL BUSINESSES</p>
          </div>
          
          {/* Seamless Sliding Banner */}
          <div className="relative">
            <div className="flex animate-scroll-seamless">
              {/* First set of logos */}
              {clientLogos.map((client, index) => (
                <div 
                  key={`first-${index}`}
                  className="flex-shrink-0 mx-6"
                >
                  <div className="w-48 h-32 bg-white/10 rounded-lg border border-white/20 hover:border-[#10B981]/50 transition-all duration-300 flex items-center justify-center group">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-white/80 group-hover:text-[#10B981] transition-colors mb-2">
                        {client.name.split(' ').map(word => word[0]).join('')}
                      </div>
                      <div className="text-xs text-gray-400 group-hover:text-gray-300">{client.industry}</div>
                    </div>
                  </div>
                </div>
              ))}
              {/* Duplicate set for seamless loop */}
              {clientLogos.map((client, index) => (
                <div 
                  key={`second-${index}`}
                  className="flex-shrink-0 mx-6"
                >
                  <div className="w-48 h-32 bg-white/10 rounded-lg border border-white/20 hover:border-[#10B981]/50 transition-all duration-300 flex items-center justify-center group">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-white/80 group-hover:text-[#10B981] transition-colors mb-2">
                        {client.name.split(' ').map(word => word[0]).join('')}
                      </div>
                      <div className="text-xs text-gray-400 group-hover:text-gray-300">{client.industry}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">WHAT WE DO</h2>
            <p className="text-xl text-gray-400">Comprehensive digital brand scaling powered by AI and human creativity</p>
          </div>

          <div className="space-y-6">
            {services.map((service, index) => (
              <Card key={index} className="bg-[#2d2d2d] border-gray-700 hover:border-[#10B981]/50 transition-all duration-300">
                <CardContent className="p-8 flex items-start gap-6">
                  <div className="text-[#10B981] flex-shrink-0">
                    {service.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-3 text-white">{service.title}</h3>
                    <p className="text-gray-400 leading-relaxed">{service.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Free Trial Section */}
      <section className="py-20 px-6 bg-[#2d2d2d]">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">START WITH A FREE 7-DAY TRIAL</h2>
          <p className="text-xl text-gray-400 mb-12 max-w-4xl mx-auto">
            Experience the TAVEEL difference risk-free. We'll create custom ads for your business, schedule professional photoshoots, 
            and show you exactly how we'll scale your brand.
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {trialFeatures.map((feature, index) => (
              <Card key={index} className="bg-[#1a1a1a] border-gray-700">
                <CardContent className="p-8 text-center">
                  <h3 className="text-2xl font-bold text-[#10B981] mb-3">{feature.title}</h3>
                  <p className="text-gray-400">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
            <Button 
              onClick={() => scrollToSection('contact')}
              className="bg-[#10B981] hover:bg-[#059669] text-white rounded-full px-8 py-6 text-lg"
            >
              CLAIM YOUR FREE TRIAL
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <span className="text-gray-400">or</span>
            <Button 
              onClick={() => window.open('https://calendly.com/taveelmedia-info/30min', '_blank')}
              className="bg-transparent border-2 border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white rounded-full px-8 py-6 text-lg transition-all duration-300"
            >
              Book a Strategy Call
            </Button>
          </div>
        </div>
      </section>

      {/* High-Performing Ads Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">HIGH-PERFORMING ADS</h2>
            <p className="text-xl text-gray-400">Real examples of ads we've created that drive results</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {adExamples.map((ad, index) => {
              const AdCounter = () => {
                const { count, countRef } = useCounter(ad.metric, 2500);
                const displayValue = ad.category === 'Fashion' 
                  ? count.toFixed(1) 
                  : Math.floor(count);
                
                return (
                  <div ref={countRef} className="text-sm text-[#22C55E] font-semibold">
                    {displayValue}{ad.metricLabel}
                  </div>
                );
              };

              return (
                <Card key={index} className="border-0 overflow-hidden hover:scale-105 transition-transform duration-300 bg-gray-900">
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={ad.image} 
                      alt={ad.category}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent"></div>
                    <div className="absolute top-4 left-4">
                      <div className="text-xs uppercase tracking-wide px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white font-semibold">
                        {ad.category}
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-6 bg-gray-900">
                    <h3 className="text-xl font-bold mb-2 text-white">{ad.title}</h3>
                    <p className="text-sm text-gray-400 mb-4">{ad.subtitle}</p>
                    <Button className="w-full bg-white text-gray-900 hover:bg-gray-100 mb-4">{ad.cta}</Button>
                    <AdCounter />
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Before/After Case Studies Section */}
      <section className="py-20 px-6 bg-[#2d2d2d]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">BEFORE & AFTER</h2>
            <p className="text-xl text-gray-400">See the dramatic difference our approach makes</p>
          </div>

          <div className="space-y-12">
            {beforeAfterCases.map((caseStudy, index) => (
              <Card key={index} className="bg-[#1a1a1a] border-gray-700 overflow-hidden">
                <CardContent className="p-0">
                  <div className="grid md:grid-cols-2">
                    {/* Before */}
                    <div className="relative">
                      <div className="absolute top-4 left-4 z-10">
                        <span className="bg-red-500/90 text-white px-4 py-2 rounded-full font-bold text-sm">BEFORE</span>
                      </div>
                      <img 
                        src={caseStudy.before.image} 
                        alt="Before"
                        className="w-full h-80 object-cover"
                      />
                      <div className="p-6 bg-red-900/20">
                        <div className="text-3xl font-bold text-red-400 mb-2">{caseStudy.before.metric}</div>
                        <p className="text-gray-400">{caseStudy.before.description}</p>
                      </div>
                    </div>

                    {/* After */}
                    <div className="relative">
                      <div className="absolute top-4 left-4 z-10">
                        <span className="bg-green-500/90 text-white px-4 py-2 rounded-full font-bold text-sm">AFTER</span>
                      </div>
                      <img 
                        src={caseStudy.after.image} 
                        alt="After"
                        className="w-full h-80 object-cover"
                      />
                      <div className="p-6 bg-green-900/20">
                        <div className="text-3xl font-bold text-[#22C55E] mb-2">{caseStudy.after.metric}</div>
                        <p className="text-gray-400">{caseStudy.after.description}</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 bg-[#10B981]/10 border-t-2 border-[#10B981]">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-gray-400 uppercase tracking-wider mb-1">{caseStudy.industry}</div>
                        <div className="text-2xl font-bold text-[#22C55E]">{caseStudy.result}</div>
                      </div>
                      <ArrowRight className="w-8 h-8 text-[#10B981]" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="py-20 px-6 bg-[#2d2d2d]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">INDUSTRIES WE SCALE</h2>
            <p className="text-xl text-gray-400">From venues to dental clinics, clothing brands to tech startups - we've done it all</p>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="relative group"
                onMouseEnter={() => setSelectedIndustry(index)}
                onMouseLeave={() => setSelectedIndustry(null)}
              >
                <button
                  className={`w-full p-4 rounded-lg border-2 transition-all duration-300 font-bold uppercase text-sm ${
                    selectedIndustry === index
                      ? 'bg-[#10B981] border-[#10B981] text-white'
                      : 'bg-[#1a1a1a] border-gray-700 text-white hover:border-[#10B981]/50'
                  }`}
                >
                  {industry.name}
                </button>

                {/* Hover Popup */}
                {selectedIndustry === index && (
                  <div className="absolute z-10 top-full left-0 right-0 mt-2 p-6 bg-[#1a1a1a] border-2 border-[#10B981] rounded-lg shadow-2xl animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-bold text-[#10B981]">Why Your Ads Aren't Scaling:</h3>
                      <div className="text-xs bg-[#10B981]/20 text-[#10B981] px-3 py-1 rounded-full font-bold">
                        {industry.activeClients} active clients
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {industry.issues.map((issue, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <span className="text-[#10B981] mt-1 flex-shrink-0">•</span>
                          <span className="text-gray-300">{issue}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section id="packages" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">CHOOSE YOUR PACKAGE</h2>
            <p className="text-xl text-gray-400">Flexible solutions designed to scale with your business needs</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <Card key={index} className={`bg-[#2d2d2d] border-gray-700 relative ${
                pkg.popular ? 'border-[#10B981] shadow-lg shadow-[#10B981]/20' : ''
              }`}>
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#10B981] text-white px-4 py-1 rounded-full text-sm font-bold">
                    MOST POPULAR
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-2 text-white">{pkg.name}</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-[#22C55E]">{pkg.price}</span>
                    <span className="text-gray-400 text-lg">{pkg.period}</span>
                  </div>
                  <p className="text-gray-400 mb-6 text-sm">{pkg.description}</p>
                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-300">
                        <span className="text-[#22C55E] mt-1">✓</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    onClick={() => scrollToSection('contact')}
                    className={`w-full rounded-full ${
                    pkg.popular 
                      ? 'bg-[#10B981] hover:bg-[#059669] text-white' 
                      : 'bg-white text-gray-900 hover:bg-gray-100'
                  }`}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-6 bg-[#2d2d2d]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">CLIENT SUCCESS STORIES</h2>
            <p className="text-xl text-gray-400">Real results from real businesses we've helped scale</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-[#1a1a1a] border-gray-700">
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[#10B981] text-[#10B981]" />
                    ))}
                  </div>
                  <p className="text-gray-300 mb-6 italic">"{testimonial.quote}"</p>
                  <div>
                    <div className="font-bold text-[#10B981]">{testimonial.author}</div>
                    <div className="text-sm text-gray-400">{testimonial.company}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">FREQUENTLY ASKED QUESTIONS</h2>
            <p className="text-xl text-gray-400">Everything you need to know before getting started</p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card 
                key={index} 
                className="bg-[#2d2d2d] border-gray-700 hover:border-[#10B981]/50 transition-all duration-300 cursor-pointer"
                onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-[#10B981] pr-4">{faq.question}</h3>
                    <div className={`text-[#10B981] transition-transform duration-300 flex-shrink-0 ${
                      openFaqIndex === index ? 'rotate-180' : ''
                    }`}>
                      <ArrowRight className="w-6 h-6 rotate-90" />
                    </div>
                  </div>
                  
                  {openFaqIndex === index && (
                    <div className="mt-4 pt-4 border-t border-gray-700">
                      <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact/CTA Section */}
      <section id="contact" className="py-20 px-6 bg-gradient-to-b from-[#2d2d2d] to-[#374151]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-bold mb-6">READY TO SCALE?</h2>
          <p className="text-xl text-gray-200 mb-12">
            Contact us today for a custom quote tailored to your business needs. Fill out the form below and we'll get back to you within 24 hours.
          </p>

          <Card className="bg-white/10 border-white/20 backdrop-blur-sm text-left">
            <CardContent className="p-8">
              <form onSubmit={handleContactSubmit} className="space-y-6">
                {/* Honeypot field - hidden from users, catches bots */}
                <input
                  type="text"
                  name="honeypot"
                  value={formData.honeypot}
                  onChange={handleInputChange}
                  style={{ display: 'none' }}
                  tabIndex="-1"
                  autoComplete="off"
                />
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-white flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      required
                      className="bg-white/10 border-white/30 text-white placeholder:text-gray-400"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-white flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Phone
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Your phone number"
                      className="bg-white/10 border-white/30 text-white placeholder:text-gray-400"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="package" className="text-white flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Package of Interest
                  </Label>
                  <Select 
                    value={formData.package} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, package: value }))}
                  >
                    <SelectTrigger className="bg-white/10 border-white/30 text-white">
                      <SelectValue placeholder="Select a package" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#2d2d2d] border-white/30">
                      <SelectItem value="Still deciding" className="text-white hover:bg-[#10B981]/20">
                        Still deciding - Need help choosing
                      </SelectItem>
                      <SelectItem value="Starter - $500/month" className="text-white hover:bg-[#10B981]/20">
                        Starter - $500/month
                      </SelectItem>
                      <SelectItem value="Professional - $1,497/month" className="text-white hover:bg-[#10B981]/20">
                        Professional - $1,497/month (Most Popular)
                      </SelectItem>
                      <SelectItem value="Enterprise - $4,997/month" className="text-white hover:bg-[#10B981]/20">
                        Enterprise - $4,997/month
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your.email@example.com"
                    required
                    className="bg-white/10 border-white/30 text-white placeholder:text-gray-400"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description" className="text-white flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    What can we help you with? *
                  </Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Tell us about your business and what you're looking to achieve..."
                    rows={5}
                    required
                    className="bg-white/10 border-white/30 text-white placeholder:text-gray-400 resize-none"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-[#10B981] hover:bg-[#059669] text-white rounded-full px-8 py-6 text-lg"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  {!isSubmitting && <ArrowRight className="w-5 h-5 ml-2" />}
                </Button>
              </form>

              <div className="mt-8 pt-8 border-t border-white/20 text-center">
                <p className="text-gray-300 mb-4">Or email us directly at</p>
                <a href="mailto:info@taveelmedia.com" className="text-[#10B981] hover:underline text-lg font-semibold block mb-6">
                  info@taveelmedia.com
                </a>
                
                <p className="text-gray-300 mb-4">Prefer to talk? Book a strategy call:</p>
                <Button 
                  onClick={() => window.open('https://calendly.com/taveelmedia-info/30min', '_blank')}
                  className="bg-white text-[#374151] hover:bg-gray-100 rounded-full px-8 py-4 text-lg font-semibold"
                >
                  Book a Call
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-[#1a1a1a] border-t border-gray-700">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400">© 2025 Taveel. All rights reserved.</p>
        </div>
      </footer>

      {/* Sticky CTA Button */}
      {showStickyButton && (
        <div className="fixed bottom-8 right-8 z-50 animate-in slide-in-from-bottom-4 duration-300">
          <Button 
            onClick={() => scrollToSection('contact')}
            className="bg-[#10B981] hover:bg-[#059669] text-white rounded-full px-8 py-6 text-lg shadow-2xl hover:scale-105 transition-transform"
          >
            Start Free Trial
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default Home;